import { useCallback, useEffect, useState } from "react";
import * as api from "./api";
import DetailView from "./components/DetailView";
import Header from "./components/Header";
import HomePage from "./components/HomePage";
import LoginPage from "./components/LoginPage";
import MobileBackNav from "./components/MobileBackNav";
import { AUTH } from "./constants";

import AddModal from "./components/AddModal";
import ConfirmDialog from "./components/ConfirmDialog";
import EditModal from "./components/EditModal";
import SeriesCalendar from "./components/SeriesCalendar";
import { PLATFORMS, PLATFORM_COLORS } from "./constants";

export default function App() {
  const [isAuth, setIsAuth] = useState(false);
  const [view, setView] = useState("home");
  const [selectedItem, setSelectedItem] = useState(null);
  const [content, setContent] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [filters, setFilters] = useState({
    platform: "",
    search: "",
    type: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ pending: 0, watched: 0 });
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const showMobileBack = view === "detail" || view === "calendar";

  // Check auth on mount
  useEffect(() => {
    const raw = localStorage.getItem(AUTH.storageKey);
    if (raw) {
      try {
        const { ts } = JSON.parse(raw);
        if (Date.now() - ts < AUTH.sessionDays * 86400000) setIsAuth(true);
        else localStorage.removeItem(AUTH.storageKey);
      } catch {
        localStorage.removeItem(AUTH.storageKey);
      }
    }
  }, []);

  // Load content
  const loadContent = useCallback(async () => {
    if (!isAuth) return;
    setLoading(true);
    setError(null);
    try {
      const params = { status: activeTab, sort: "rating" };
      if (filters.platform) params.platform = filters.platform;
      if (filters.search) params.search = filters.search;
      if (filters.type) params.type = filters.type;
      const data = await api.getContent(params);
      setContent(data);
      const [p, w] = await Promise.all([
        api.getContent({ status: "pending" }),
        api.getContent({ status: "watched" }),
      ]);
      setStats({ pending: p.length, watched: w.length });
    } catch (err) {
      setError("Error cargando contenido: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuth, activeTab, filters]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Auth
  const handleLogin = (user, pass) => {
    if (user === AUTH.user && pass === AUTH.pass) {
      localStorage.setItem(AUTH.storageKey, JSON.stringify({ ts: Date.now() }));
      setIsAuth(true);
      return true;
    }
    return false;
  };
  const handleLogout = () => {
    localStorage.removeItem(AUTH.storageKey);
    setIsAuth(false);
    setView("home");
  };

  // Navigation
  const goToDetail = (item) => {
    setSelectedItem(item);
    setView("detail");
  };
  const goHome = () => {
    setView("home");
    setSelectedItem(null);
  };

  // CRUD
  const handleAdd = async (data) => {
    await api.createContent(data);
    setShowAdd(false);
    loadContent();
  };

  const handleEdit = async (id, data) => {
    await api.updateContent(id, data);
    setEditItem(null);
    loadContent();
    if (view === "detail" && selectedItem && selectedItem.id === id) {
      const updated = await api.getContentById(id);
      setSelectedItem(updated);
    }
  };

  const handleDelete = (item) => {
    setConfirm({
      type: "delete",
      title: item.title,
      onConfirm: async () => {
        await api.deleteContent(item.id);
        setConfirm(null);
        if (view === "detail") goHome();
        loadContent();
      },
    });
  };

  const handleMarkWatched = (item) => {
    setConfirm({
      type: "watched",
      title: item.title,
      onConfirm: async () => {
        await api.updateStatus(item.id, "watched");
        setConfirm(null);
        if (view === "detail") goHome();
        loadContent();
      },
    });
  };

  const handleMarkPending = async (item) => {
    await api.updateStatus(item.id, "pending");
    if (view === "detail") goHome();
    loadContent();
  };

  if (!isAuth) return <LoginPage onLogin={handleLogin} />;

  return (
    <div className={`min-h-screen bg-cinema-bg ${showMobileBack ? "pb-24 sm:pb-0" : ""}`}>
      {view === "detail" && selectedItem ? (
        <DetailView
          item={selectedItem}
          onBack={goHome}
          onEdit={(it) => setEditItem(it)}
          onDelete={handleDelete}
          onMarkWatched={handleMarkWatched}
          onMarkPending={handleMarkPending}
        />
      ) : view === "calendar" ? (
        <>
          <Header
            onAdd={() => setShowAdd(true)}
            onLogout={handleLogout}
            stats={stats}
            onHome={goHome}
          />
          <SeriesCalendar onBack={goHome} onCardClick={goToDetail} />
        </>
      ) : (
        <>
          <Header
            onAdd={() => setShowAdd(true)}
            onLogout={handleLogout}
            stats={stats}
            onHome={goHome}
          />
          <HomePage
            content={content}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            filters={filters}
            onFiltersChange={setFilters}
            loading={loading}
            stats={stats}
            onCardClick={goToDetail}
            onEdit={(it) => setEditItem(it)}
            onDelete={handleDelete}
            onMarkWatched={handleMarkWatched}
            onMarkPending={handleMarkPending}
            onAdd={() => setShowAdd(true)}
            onCalendar={() => setView("calendar")}
          />
        </>
      )}
      {showMobileBack && <MobileBackNav onBack={goHome} />}

      {showAdd && (
        <AddModal
          onClose={() => setShowAdd(false)}
          onAdd={handleAdd}
          PLATFORMS={PLATFORMS}
          PLATFORM_COLORS={PLATFORM_COLORS}
        />
      )}
      {editItem && (
        <EditModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSave={handleEdit}
        />
      )}
      {confirm && (
        <ConfirmDialog
          type={confirm.type}
          title={confirm.title}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {error && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-red-900/90 border border-red-700 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-slide-up">
          <span className="flex-1 text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-300 hover:text-white p-1"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
